import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Spinner } from 'react-bootstrap';
import { getFirestore, doc, getDoc, updateDoc, arrayUnion } from 'firebase/firestore';
import axios from 'axios';

const ManualPayment = ({
    theme,
    userEmail,
    userName,
    typeOfMembership,
    expirationDate,
    profileId,
    onSendToParent
}) => {
    const [show, setShow] = useState(false);
    const [loading, setLoading] = useState(false);
    const [fetchingRates, setFetchingRates] = useState(false);
    const [rates, setRates] = useState({ amount: null, lateFee: null });
    const [note, setNote] = useState("");
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);

    const db = getFirestore();
    const formatter = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });

    const handleClose = () => {
        setShow(false);
        setSuccess(false);
        setError(null);
        setNote("");
    };
    const handleShow = () => setShow(true);

    useEffect(() => {
        if (show && typeOfMembership) {
            const fetchRates = async () => {
                setFetchingRates(true);
                setError(null);
                try {
                    const levelDocRef = doc(db, "membership_levels", typeOfMembership);
                    const levelSnap = await getDoc(levelDocRef);
                    if (levelSnap.exists()) {
                        const data = levelSnap.data();
                        setRates({ amount: Number(data.cost) || 0, lateFee: Number(data.lateFee) || 0 });
                    }
                } catch (err) { setError("Rate lookup failed."); }
                finally { setFetchingRates(false); }
            };
            fetchRates();
        }
    }, [show, typeOfMembership, db]);

    const today = new Date();
    const currentExp = expirationDate ? new Date(expirationDate) : new Date();
    const isExpired = currentExp < today;
    const finalTotal = rates.amount !== null ? (isExpired ? (rates.amount + rates.lateFee) : rates.amount) : 0;

    const handleManualPayment = async () => {
        setLoading(true);
        setError(null);

        // CRA requires process.env.REACT_APP_ prefix
        const serviceId = process.env.REACT_APP_EMAILJS_SERVICE_ID;
        const templateId = process.env.REACT_APP_EMAILJS_TEMPLATE_ID;
        const publicKey = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;

        try {
            const docRef = doc(db, "profiles", profileId);
            let baseDate = new Date(isExpired ? today : currentExp);
            baseDate.setFullYear(baseDate.getFullYear() + 1);
            const newExpDate = baseDate.toISOString().split('T')[0];
            const transactionId = `CASH-${Math.random().toString(36).substr(2, 7).toUpperCase()}`;

            // 1. UPDATE FIRESTORE FIRST
            await updateDoc(docRef, {
                expirationDate: newExpDate,
                membershipStatus: 'active',
                transactionLogs: arrayUnion({
                    amount: finalTotal.toFixed(2),
                    date: new Date().toLocaleDateString('en-US'),
                    ref: transactionId,
                    type: 'Manual Renewal',
                    membershipType: typeOfMembership,
                    status: 'paid',
                    newExpDate: newExpDate,
                    paymentMethod: 'Cash/Check',
                    staffNote: note
                })
            });

            // 2. TRIGGER EMAIL (Only after successful DB update)
            if (serviceId && templateId && publicKey) {
                try {
                    await axios.post('https://api.emailjs.com/api/v1.0/email/send', {
                        service_id: serviceId,
                        template_id: templateId,
                        user_id: publicKey,
                        template_params: {
                            user_name: userName || "Member",
                            user_email: userEmail || "",
                            membership_type: typeOfMembership || "Member",
                            amount: finalTotal.toFixed(2),
                            new_expiration: newExpDate,
                            transaction_id: transactionId,
                            staff_note: note || "No note provided"
                        }
                    }, {
                        headers: { 'Content-Type': 'application/json' }
                    });
                } catch (emailErr) {
                    console.error("EmailJS Service Error:", emailErr.response?.data || emailErr.message);
                    // We don't block success here because the payment IS recorded in DB
                }
            } else {
                // If this logs, the issue is definitely the .env file configuration
                console.warn("Email attempted but keys are undefined. Check .env names and restart server.");
            }

            onSendToParent(true)

            setSuccess(true);
            setTimeout(() => { handleClose(); }, 2000);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <Button
                variant="outline-success"
                className="rounded-circle shadow-sm p-0"
                style={{ width: '40px', height: '40px', lineHeight: '38px' }}
                onClick={handleShow}
            >
                $
            </Button>

            <Modal
                show={show}
                onHide={handleClose}
                centered
                contentClassName={theme === 'dark' ? 'bg-dark text-white' : ''}
                backdropClassName='adminBackdrop'
            >
                <Modal.Header closeButton closeVariant={theme === 'dark' ? 'white' : undefined} className="">
                    <Modal.Title className="orange font-bold h5">Manual Payment Log</Modal.Title>
                </Modal.Header>

                <Modal.Body className="p-4">
                    {fetchingRates ? (
                        <div className="text-center py-5">
                            <Spinner animation="border" variant="warning" />
                            <p className="mt-2 text-muted small text-uppercase">Syncing Rates...</p>
                        </div>
                    ) : (
                        <>
                            <div className="mb-4">
                                <label className="text-muted small text-uppercase d-block mb-1" style={{ fontSize: '10px' }}>Member</label>
                                <p className="h5 font-bold m-0">{userName}</p>
                                <p className="text-info small mb-0">{typeOfMembership}</p>

                            </div>

                            <div className="p-4 mb-4 text-center" style={{ background: 'rgba(255,255,255,0.05)', borderRadius: '6px', border: '1px solid rgba(255,255,255,0.1)' }}>
                                <label className="text-muted small text-uppercase d-block mb-1" style={{ fontSize: '10px' }}>Total Collected</label>
                                <h1 className="display-4 font-mono font-bold mb-2">{formatter.format(finalTotal)}</h1>
                                <span className={`badge ${isExpired ? 'bg-danger' : 'bg-success'}`}>
                                    {isExpired ? 'EXPIRED + LATE FEE' : 'ACTIVE (TIME ADDED)'}
                                </span>
                            </div>

                            <Form.Group>
                                <Form.Label className="text-muted small text-uppercase" style={{ fontSize: '10px' }}>Payment Note</Form.Label>
                                <Form.Control
                                    type="text"
                                    className="bg-dark text-white border-secondary shadow-none"
                                    placeholder="e.g. Cash / Check #123"
                                    value={note}
                                    onChange={(e) => setNote(e.target.value)}
                                />
                            </Form.Group>
                        </>
                    )}

                    {error && <span variant="danger" className=" mt-3 py-2 small">{error}</span>}
                    {success && <span variant="success" className=" mt-3 py-2 small">✅ Payment Recorded & Email Sent</span>}
                </Modal.Body>

                <Modal.Footer className="border-secondary">
                    {!success && (
                        <Button
                            variant="info"
                            className="px-4 font-bold text-white"
                            onClick={handleManualPayment}
                            disabled={loading || fetchingRates || rates.amount === null}
                        >
                            {loading ? <Spinner animation="border" size="sm" className="me-2" /> : 'Confirm Payment'}
                        </Button>
                    )}
                    <Button variant="secondary" onClick={handleClose}>
                        {success ? 'Close' : 'Cancel'}
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
};

export default ManualPayment;